import { PayloadAction, SimpleAction } from 'interfaces/action'
import { Collection } from 'interfaces'
import { Car, CarMake, CarModel, Home, HomePrice } from 'interfaces/property'
import { createAction, createPayloadAction } from 'helpers/actionsHelpers'

// ===========================================================================//
//                                     HOMES                                  //
// ===========================================================================//

export const SET_HOME_PRICE = 'SET_HOME_PRICE' as const
type SetHomePrice = PayloadAction<typeof SET_HOME_PRICE, { key: string, price: HomePrice }>
export const setHomePriceActionCreator = (key: string, price: HomePrice) =>
  createPayloadAction(SET_HOME_PRICE, { price, key })

export const GET_HOMES = 'GET_HOMES' as const
type GetHomes = SimpleAction<typeof GET_HOMES>
export const getHomesActionCreator = (): GetHomes => createAction(GET_HOMES)

export const SET_HOMES = 'SET_HOMES' as const
type SetHomes = PayloadAction<typeof SET_HOMES, Collection<Home, 'id'>>
export const setHomesActionCreator = (homes: Collection<Home, 'id'>): SetHomes =>
  createPayloadAction(SET_HOMES, homes)

export const UNLINK_HOME = 'UNLINK_HOME' as const
type UnlinkHome = PayloadAction<typeof UNLINK_HOME, { id: number, date: string }>
export const unlinkHomeActionCreator = (id: number, date: string): UnlinkHome =>
  createPayloadAction(UNLINK_HOME, { id, date })

export const HOME_UNLINKED = 'HOME_UNLINKED' as const
type HomeUnlinked = PayloadAction<typeof HOME_UNLINKED, number>
export const homeUnlinkedActionCreator = (id: number): HomeUnlinked =>
  createPayloadAction(HOME_UNLINKED, id)

export const FAIL_UNLINK_HOME = 'FAIL_UNLINK_HOME' as const
type FailUnlinkHome = PayloadAction<typeof FAIL_UNLINK_HOME, number>
export const failUnlinkHomeActionCreator = (id: number): FailUnlinkHome =>
  createPayloadAction(FAIL_UNLINK_HOME, id)

export const DELETE_HOME = 'DELETE_HOME' as const
type DeleteHome = PayloadAction<typeof DELETE_HOME, number>
export const deleteHomeActionCreator = (id: number): DeleteHome =>
  createPayloadAction(DELETE_HOME, id)

export const HOME_DELETED = 'HOME_DELETED' as const
type HomeDeleted = PayloadAction<typeof HOME_DELETED, number>
export const homeDeletedActionCreator = (id: number): HomeDeleted =>
  createPayloadAction(HOME_DELETED, id)

export const FAIL_DELETE_HOME = 'FAIL_DELETE_HOME' as const
type FailDeleteHome = PayloadAction<typeof FAIL_DELETE_HOME, Home>
export const failDeleteHomeActionCreator = (home: Home): FailDeleteHome =>
  createPayloadAction(FAIL_DELETE_HOME, home)

// ===========================================================================//
//                                     CARS                                   //
// ===========================================================================//

export const GET_CARS = 'GET_CARS' as const
type GetCars = SimpleAction<typeof GET_CARS>
export const getCarsActionCreator = (): GetCars => createAction(GET_CARS)

export const SET_CARS = 'SET_CARS' as const
type SetCars = PayloadAction<typeof SET_CARS, Collection<Car, 'id'>>
export const setCarsActionCreator = (cars: Collection<Car, 'id'>): SetCars =>
  createPayloadAction(SET_CARS, cars)

// Settings
// ---------------------------------------------------------------------------

export const UNLINK_CAR = 'UNLINK_CAR' as const
type UnlinkCar = PayloadAction<typeof UNLINK_CAR, { id: number, date: string }>
export const unlinkCarActionCreator = (id: number, date: string): UnlinkCar =>
  createPayloadAction(UNLINK_CAR, { id, date })

export const CAR_UNLINKED = 'CAR_UNLINKED' as const
type CarUnlinked = PayloadAction<typeof CAR_UNLINKED, number>
export const carUnlinkedActionCreator = (id: number): CarUnlinked =>
  createPayloadAction(CAR_UNLINKED, id)

export const FAIL_UNLINK_CAR = 'FAIL_UNLINK_CAR' as const
type FailUnlinkCar = PayloadAction<typeof FAIL_UNLINK_CAR, number>
export const failUnlinkCarActionCreator = (id: number): FailUnlinkCar =>
  createPayloadAction(FAIL_UNLINK_CAR, id)

export const DELETE_CAR = 'DELETE_CAR' as const
type DeleteCar = PayloadAction<typeof DELETE_CAR, number>
export const deleteCarActionCreator = (id: number): DeleteCar =>
  createPayloadAction(DELETE_CAR, id)

export const CAR_DELETED = 'CAR_DELETED' as const
type CarDeleted = PayloadAction<typeof CAR_DELETED, number>
export const carDeletedActionCreator = (id: number): CarDeleted =>
  createPayloadAction(CAR_DELETED, id)

export const FAIL_DELETE_CAR = 'FAIL_DELETE_CAR' as const
type FailDeleteCar = PayloadAction<typeof FAIL_DELETE_HOME, Car>
export const failDeleteCarActionCreator = (car: Car): FailDeleteCar =>
  createPayloadAction(FAIL_DELETE_HOME, car)


// Onboarding
// ---------------------------------------------------------------------------

export const GET_CAR_MAKES = 'GET_CAR_MAKES' as const
type GetCarMakes = SimpleAction<typeof GET_CAR_MAKES>
export const getCarMakesActionCreator = () =>
  createAction(GET_CAR_MAKES)

export const SET_CAR_MAKES = 'SET_CAR_MAKES' as const
type SetCarMakes = PayloadAction<typeof SET_CAR_MAKES, CarMake[]>
export const setCarMakesActionCreator = (payload: CarMake[]) =>
  createPayloadAction(SET_CAR_MAKES, payload)

export const GET_CAR_MODELS = 'GET_CAR_MODELS' as const
type GetCarModels = PayloadAction<typeof GET_CAR_MODELS, number>
export const getCarModelsActionCreator = (payload: number) =>
  createPayloadAction(GET_CAR_MODELS, payload)

export const SET_CAR_MODELS = 'SET_CAR_MODELS' as const
type SetCarModels = PayloadAction<typeof SET_CAR_MODELS, { make: number, models: CarModel[] }>
export const setCarModelsActionCreator = (make: number, models: CarModel[]) =>
  createPayloadAction(SET_CAR_MODELS, { make, models })

export const RESET_PROPERTY = 'RESET_PROPERTY' as const
type ResetProperty = SimpleAction<typeof RESET_PROPERTY>
export const resetPropertyActionCreator = (): ResetProperty => createAction(RESET_PROPERTY)

type Action =
  | GetHomes
  | SetHomes
  | DeleteHome
  | HomeDeleted
  | FailDeleteHome
  | UnlinkHome
  | HomeUnlinked
  | FailUnlinkHome
  | SetHomePrice

  | GetCars
  | SetCars
  | UnlinkCar
  | CarUnlinked
  | FailUnlinkCar
  | DeleteCar
  | CarDeleted
  | FailDeleteCar

  | GetCarMakes
  | SetCarMakes
  | GetCarModels
  | SetCarModels
  | ResetProperty

export default Action
